/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback } from 'preact/hooks';

import { CurrentKeyDetail } from '../UNSAFE_Collection';

/**
 * Hook for handling current key update due to user interaction including keyboard navigation.
 *
 * @param keyExtractor function to extract the key based on the provided element
 * @param getPrevKey function to get the previous key based on the current key
 * @param getNextKey function to get the next key based on the current key
 * @param getFirstVisibleKey function to get the key of the first available item
 * @param getLastVisibleKey function to get the key of the last available item
 * @param currentKey the current key
 * @param onChange function to invoke if the current key has changed
 * @returns
 */
export function useMenuCurrentKey(
  keyExtractor: (element: HTMLElement) => string | null,
  getPrevKey: () => string | null,
  getNextKey: () => string | null,
  getFirstVisibleKey: () => string | null,
  getLastVisibleKey: () => string | null,
  currentKey?: string,
  onChange?: (detail: CurrentKeyDetail<string>) => void
) {
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (onChange) {
        // TODO: support horizontal layout on icon menu (left/right arrow key)
        if (event.key === 'ArrowDown' && !event.shiftKey) {
          const nextKey = getNextKey();
          if (nextKey && currentKey != nextKey) {
            onChange({ value: nextKey });
          }
          event.preventDefault();
        } else if (event.key === 'ArrowUp' && !event.shiftKey) {
          const prevKey = getPrevKey();
          if (prevKey && currentKey != prevKey) {
            onChange({ value: prevKey });
          }
          event.preventDefault();
        } else if (event.key === 'Home') {
          const firstKey = getFirstVisibleKey();
          if (firstKey && currentKey != firstKey) {
            onChange({ value: firstKey });
          }
          event.preventDefault();
        } else if (event.key === 'End') {
          const lastKey = getLastVisibleKey();
          if (lastKey && currentKey != lastKey) {
            onChange({ value: lastKey });
          }
          event.preventDefault();
        }
      }
    },
    [currentKey, getNextKey, getPrevKey, onChange, getFirstVisibleKey, getLastVisibleKey]
  );
  //Capture needed since usePress stop propagation, what produces not triggering this event.
  //We can't stop propagation here, because if we do that, usePress onClick will not be triggered.
  const onClickCapture = useCallback(
    (event: MouseEvent) => {
      if (onChange && !event.shiftKey) {
        const nextKey = keyExtractor(event.target as HTMLElement);
        if (nextKey && currentKey != nextKey) {
          onChange({ value: nextKey });
        }
      }
    },
    [currentKey, keyExtractor, onChange]
  );

  const currentKeyProps = onChange == null ? {} : { onClickCapture, onKeyDown };
  return { currentKeyProps };
}
